<script>
	import { scaleLinear } from "d3-scale";

	export let type = "stackedColumn";
	export let id;
	export let row;
	export let dataCharts;

	$: key = row.measure;

	function isNumericValue(value) {
		if (value === null || value === undefined || value === "") return false;
		if (typeof value === "string" && value.trim() === "") return false;
		return Number.isFinite(+value) && +value !== 0;
	}

	$: categories = dataCharts.columns.filter((column) =>
		column.startsWith(key),
	);

	// Only plot rows that contain a real value in at least one stacked series.
	$: chartRows = dataCharts.filter((entry) =>
		categories.some((category) => isNumericValue(entry[category])),
	);

	// Keep index alignment across the plotted rows while treating blanks as 0.
	$: seriesValues = categories.map((category) =>
		chartRows.map((entry) => (isNumericValue(entry[category]) ? +entry[category] : 0)),
	);

	$: pointCount = chartRows.length;
	$: stackedTotals = Array.from({ length: pointCount }, (_, i) =>
		seriesValues.reduce((sum, series) => sum + (series[i] || 0), 0),
	);
	$: stackedBars = Array.from({ length: pointCount }, (_, i) => {
		let cumulative = min;
		return seriesValues.map((series, seriesIndex) => {
			const value = series[i] || 0;
			const start = cumulative;
			cumulative += value;
			return {
				seriesIndex,
				value,
				start,
				end: cumulative,
			};
		});
	});

	$: max = row.yaxisend;
	$: min = row.yaxisstart;
	$: range = max - min;

	let chartWidth = 300,
		height = 110,
		offset = 35,
		rightPosition = 90;
	let el;

	$: width = chartWidth - offset - rightPosition;
	$: barWidth =
		pointCount > 1
			? 9 * (width / ((pointCount - 1) * 10 + 9))
			: Math.max(8, width * 0.25);

	$: yScale = scaleLinear().domain([min, max]).range([0, height]);

	$: decimalPlacesRaw = row?.decimalPlaces ?? row?.decimalPlace;
	$: decimalPlaces = Number.isFinite(+decimalPlacesRaw)
		? Math.max(0, Math.min(20, Math.trunc(+decimalPlacesRaw)))
		: 1;

	function formatWithDecimalPlaces(value) {
		const numberValue = +value;
		return Number.isFinite(numberValue)
			? numberValue.toFixed(decimalPlaces)
			: value;
	}

	$: textLabel =
		stackedTotals.length > 0
			? formatWithDecimalPlaces(stackedTotals[stackedTotals.length - 1]) +
				(row.directLabelUnit?.includes("%")
					? row.directLabelUnit
					: row.directLabelUnit
						? " " + row.directLabelUnit
						: "")
			: "";

	const fallbackColours = [
		"rgb(32, 96, 149)",
		"rgb(17, 140, 123)",
		"rgb(246, 96, 104)",
		"rgb(46, 161, 164)",
		"rgb(103, 73, 166)",
		"rgb(135, 26, 91)",
	];

	function getSeriesColor(category, index) {
		if (!category) return fallbackColours[index % fallbackColours.length];
		if (category.includes("Female")) return "rgb(103, 73, 166)";
		if (category.includes("Male")) return "rgb(46, 161, 164)";
		if (category.includes("sea")) return "rgb(17, 140, 123)";
		if (category.includes("land")) return "rgb(32, 96, 149)";
		if (category.includes("rural")) return "rgb(135, 26, 91)";
		if (category.includes("urban")) return "rgb(32, 96, 149)";
		return fallbackColours[index % fallbackColours.length];
	}

	function formatDate(dateStr) {
		if (!dateStr) return dateStr;

		const str = String(dateStr);

		const monthMap = {
			jan: "Jan",
			feb: "Feb",
			mar: "Mar",
			apr: "Apr",
			may: "May",
			jun: "June",
			jul: "July",
			aug: "Aug",
			sep: "Sept",
			oct: "Oct",
			nov: "Nov",
			dec: "Dec",
		};

		const longMatch = str.match(/^(\d{1,2})-([A-Za-z]+)-(\d{2})$/);
		if (longMatch) {
			const month = monthMap[longMatch[2].toLowerCase()];
			const year = 2000 + parseInt(longMatch[3]);
			if (!month) return str;
			return `${parseInt(longMatch[1])} ${month} ${year}`;
		}

		const noYearMatch = str.match(/^(\d{1,2})-([A-Za-z]+)$/);
		if (noYearMatch) {
			const month = monthMap[noYearMatch[2].toLowerCase()];
			if (!month) return str;
			return `${parseInt(noYearMatch[1])} ${month}`;
		}

		const shortMatch = str.match(/^([A-Za-z]+)-(\d{2})$/);
		if (shortMatch) {
			const month = monthMap[shortMatch[1].toLowerCase()];
			const year =
				parseInt(shortMatch[2]) >= 30
					? 1900 + parseInt(shortMatch[2])
					: 2000 + parseInt(shortMatch[2]);
			if (!month) return str;
			return `${month} ${year}`;
		}

		return str;
	}

	$: formattedStartDate = formatDate(row.startdate);
	$: formattedEndDate = formatDate(row.enddate);
	$: formattedUpdated = formatDate(row.update);
</script>

<div class="graphic" {id} bind:this={el} bind:clientWidth={chartWidth}>
	<div class="chartContainer">
		<p class="headingText">{row.headingtext}</p>
	</div>

	<svg class="chartDrawing" width="281">
		<rect class="chartBackground" x={offset} {width} {height}></rect>

		<line
			x1={offset}
			x2={offset}
			y1="0"
			y2={height}
			stroke="black"
			stroke-width="0.5px"
		/>
		<line x1={offset - 5} x2={offset} y1="0" y2="0" stroke="grey" />
		<line
			x1={offset - 5}
			x2={offset}
			y1={height}
			y2={height}
			stroke="grey"
		/>
		<text
			x={offset - 5}
			y="0"
			fill="black"
			text-anchor="end"
			dominant-baseline="middle">{row.yaxisend}</text
		>
		<text
			x={offset - 5}
			y={height}
			fill="black"
			text-anchor="end"
			dominant-baseline="middle">{row.yaxisstart}</text
		>

		{#if !row.yaxisstart}
			<line
				x1={offset}
				x2={offset + width}
				y1={height}
				y2={height}
				stroke="lightgrey"
				stroke-width="3px"
			/>
		{/if}

		<line
			x1={offset + width - barWidth / 2}
			x2={offset + width - barWidth / 2}
			y1={height}
			y2="115"
			stroke="grey"
		/>
		<line
			x1={offset + barWidth / 2}
			x2={offset + barWidth / 2}
			y1={height}
			y2="115"
			stroke="grey"
		/>
		<text
			class="startDate"
			x={offset + barWidth / 2}
			y={height}
			dy="1.5em"
			dominant-baseline="top">{formattedStartDate}</text
		>
		<text
			class="endDate"
			x={offset + width - barWidth / 2}
			y={height}
			dy="1.5em"
			dominant-baseline="top">{formattedEndDate}</text
		>

		{#each stackedBars as segments, i}
			{@const x = offset + (i * barWidth * 10) / 9}

			{#each segments as segment}
				{#if segment.value > 0}
					<rect
						x={x}
						y={height - yScale(segment.end)}
						width={barWidth}
						height={yScale(segment.end) - yScale(segment.start)}
						fill={getSeriesColor(categories[segment.seriesIndex], segment.seriesIndex)}
					/>
				{/if}
			{/each}
		{/each}

		{#if textLabel}
			<text
				class="endText"
				x={offset + width - 10}
				y={height - ((stackedTotals[stackedTotals.length - 1] - min) / range) * height}
				fill="rgb(51, 51, 51)"
				text-anchor="start"
				dx="1em"
				dominant-baseline="middle"
				font-weight="700">{textLabel}</text
			>
		{/if}
	</svg>

	<p class="updated">Updated: {formattedUpdated}</p>
</div>
